import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ImageIcon, Loader2, Trash2, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";
import { fileToImageAssets, formatRelativeTime, firstLine, uploadProjectMedia, entryThumbSrc } from "../lib/media";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import ConfirmDialog from "./ConfirmDialog";
import { useAuth } from "../context/AuthContext";

const STATE_LABELS = [
    { id: "before", label: "Before" },
    { id: "during", label: "During" },
    { id: "after", label: "After" },
    { id: "reset", label: "Reset" },
    { id: "custom", label: "Custom" },
];

function labelFor(entry) {
    if (entry.state_label === "custom") return entry.custom_label || "Custom";
    return entry.state_label.charAt(0).toUpperCase() + entry.state_label.slice(1);
}

const StateStream = forwardRef(function StateStream({ projectId, mode, sceneId, characterId, characterName, isOwner = false, backHref, backLabel, viewOnly = false, streamLabelOverride = null }, ref) {
    const apiBase = useApiBase();
    const routeBase = useRouteBase();
    const { user } = useAuth();
    const captureRef = useRef(null);
    const galleryRef = useRef(null);
    const formRef = useRef(null);

    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // pending entry form state
    const [pendingFile, setPendingFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [label, setLabel] = useState("during");
    const [customLabel, setCustomLabel] = useState("");
    const [look, setLook] = useState("");
    const [note, setNote] = useState("");
    const [uploading, setUploading] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const isPrep = mode === "prep";

    const refresh = async () => {
        const params = { mode, character_id: characterId };
        if (mode === "onset" && sceneId) params.scene_id = sceneId;
        const { data } = await api.get(`${apiBase}/${projectId}/state-entries`, { params });
        setEntries(data);
    };

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const params = { mode, character_id: characterId };
                if (mode === "onset" && sceneId) params.scene_id = sceneId;
                const { data } = await api.get(`${apiBase}/${projectId}/state-entries`, { params });
                if (!cancelled) setEntries(data);
            } catch (e) {
                if (!cancelled) setError(formatApiError(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [projectId, mode, sceneId, characterId]);

    useEffect(() => {
        if (!pendingFile) {
            setPreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(pendingFile);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [pendingFile]);

    const pickFile = () => {
        // Iter-122 — camera capture disabled globally for the
        // pilot. Both the primary "Upload image" and the legacy
        // "From photos" paths now open the photo library / file
        // picker — kept ref-distinct only so existing automation
        // targeting either testid keeps working.
        // Iter-157J.3 — Option B from the pilot trust spec: if a
        // draft is already in flight when the user taps Upload
        // Fitting Photos again, we DO NOT open a second picker
        // (which would replace `pendingFile` and silently destroy
        // the existing draft). Instead we scroll the existing
        // draft form back into view so the user finishes the
        // entry they started. No double-drafts, no silent loss.
        if (pendingFile) {
            try {
                formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            } catch { /* ignore */ }
            return;
        }
        galleryRef.current?.click();
    };

    const onFile = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            setError("Please choose an image.");
            return;
        }
        setError("");
        setPendingFile(file);
        setLabel("during");
        setCustomLabel("");
        setLook("");
        setNote("");
    };

    const cancelPending = () => {
        setPendingFile(null);
        setLabel("during");
        setCustomLabel("");
        setLook("");
        setNote("");
        setError("");
    };

    // Iter-157J.3 — Parent surfaces (PrepMode drawer) call
    // streamRef.current.hasDraft() before any action that would
    // silently destroy a pending fitting entry (character switch,
    // drawer close, navigate away). discardDraft() is invoked
    // after the parent's ConfirmDialog confirms the user really
    // wants to abandon the work.
    useImperativeHandle(
        ref,
        () => ({
            hasDraft: () => Boolean(pendingFile),
            discardDraft: () => cancelPending(),
        }),
        [pendingFile],
    );

    // Iter-157J.3 — browser-level navigation guard. While a draft
    // is live, hooking beforeunload triggers the native "Leave
    // site?" prompt on tab close / hard reload so the work is
    // not destroyed by an accidental swipe-away. This is the
    // only place we touch window navigation; in-app navigation
    // is guarded by the parent's ConfirmDialog flow.
    useEffect(() => {
        if (!pendingFile) return undefined;
        const onBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = "";
            return "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [pendingFile]);

    const save = async () => {
        if (!pendingFile) return;
        // Iter-157J.3 — In prep (Fittings) mode we hide the
        // continuity state chips entirely (they make no sense for
        // pre-shoot fitting captures — "Before what? Reset what?").
        // We pin state_label="custom" + custom_label=null so the
        // backend StateEntryCreate is happy without burdening the
        // H&M user with a meaningless choice.
        if (!isPrep && label === "custom" && !customLabel.trim()) {
            setError("Enter a custom label.");
            return;
        }
        setUploading(true);
        setError("");
        try {
            const { full, thumb } = await fileToImageAssets(pendingFile);
            // Upload the ORIGINAL file to GridFS so quality is preserved and
            // the user can later download the source.
            const media = await uploadProjectMedia(projectId, pendingFile, {
                display: full,
                thumbnail: thumb,
                apiBase,
            });
            const body = {
                mode,
                character_id: characterId,
                scene_id: mode === "onset" ? sceneId : null,
                state_label: isPrep ? "custom" : label,
                custom_label: isPrep
                    ? null
                    : label === "custom"
                        ? customLabel.trim()
                        : null,
                look: isPrep ? (look.trim() || null) : null,
                note: note.trim() || null,
                media_id: media.id,
            };
            const { data } = await api.post(`${apiBase}/${projectId}/state-entries`, body);
            setEntries((prev) => [data, ...prev]);
            cancelPending();
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setUploading(false);
        }
    };

    const removeEntry = async (entry) => {
        // Iter-155B.0 — show calm Shadcn confirm rather than the
        // native window.confirm. Actual delete happens after the
        // user confirms via the dialog.
        setPendingDelete(entry);
    };

    const confirmRemoveEntry = async () => {
        const entry = pendingDelete;
        if (!entry) return;
        setDeleting(true);
        try {
            await api.delete(`${apiBase}/${projectId}/state-entries/${entry.id}`);
            setEntries((prev) => prev.filter((e) => e.id !== entry.id));
            setPendingDelete(null);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setDeleting(false);
        }
    };

    const streamLabel = streamLabelOverride
        ? streamLabelOverride
        : mode === "onset"
            ? "State Stream"
            : "Fittings (Unassigned)";

    return (
        <section data-testid="state-stream" className="space-y-4">
            <input
                ref={galleryRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/heic,image/heif,image/gif"
                multiple
                className="hidden"
                data-testid="state-gallery-input"
                onChange={onFile}
            />
            {/* Iter-122 — `state-capture-input` retained as a hidden
                no-op input for backwards compat with any test that
                queries the testid. The `capture` attribute (which
                opens the OS camera) is intentionally absent so even
                if a stale handler `.click()`s it, the device just
                opens the photo library.
                Iter-124 — explicit MIME list (not `image/*`) narrows
                the OS picker sheet to library-only on most browsers. */}
            <input
                ref={captureRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/heic,image/heif,image/gif"
                className="hidden"
                data-testid="state-capture-input"
                onChange={onFile}
            />

            <div className="flex items-center justify-between">
                <h3 className="font-display text-base font-bold text-white">
                    {streamLabel}
                </h3>
                <span
                    data-testid="state-stream-count"
                    className="text-[10px] uppercase tracking-[0.25em] text-neutral-500"
                >
                    {entries.length}
                </span>
            </div>

            {error ? (
                <p data-testid="state-stream-error" className="text-sm text-red-400">
                    {error}
                </p>
            ) : null}

            {/* Iter-124 — contextual Upload action restored (single
                button). The primary tap surface is the `state-capture-btn`
                testid so existing automation stays green. The
                `state-capture-input` + `state-gallery-input` hidden
                inputs remain identical in behaviour — both open the
                photo library (no `capture` attr), see iter-122.
                Iter-157J — when prep-mode and the stream is EMPTY,
                we suppress this small top button and let the big
                prominent empty-state CTA below carry the upload
                affordance instead so first-time H&M users have a
                single, obvious target. The small button stays once
                photos exist so add-more lives inline. */}
            {!pendingFile ? (
                viewOnly ? null : (
                    !loading && mode === "prep" && entries.length === 0 ? null : (
                        <div data-testid="state-capture-actions" className="grid grid-cols-1 gap-2">
                            <button
                                type="button"
                                data-testid="state-capture-btn"
                                data-action="upload"
                                onClick={pickFile}
                                className="tap min-tap rounded-xl bg-primary text-primary-foreground font-semibold inline-flex items-center justify-center gap-2 shadow-sm hover:opacity-95"
                            >
                                <ImageIcon size={18} />
                                {mode === "prep" ? "Upload fitting photo" : "Upload image"}
                            </button>
                        </div>
                    )
                )
            ) : (
                <div
                    ref={formRef}
                    data-testid="state-entry-form"
                    className="rounded-xl border border-white/10 bg-card p-4 space-y-4"
                >
                    <div className="flex items-start gap-3">
                        {previewUrl ? (
                            <img
                                src={previewUrl}
                                alt="preview"
                                className="h-24 w-24 object-cover rounded-lg border border-white/10"
                            />
                        ) : null}
                        <div className="min-w-0 flex-1">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                                New {mode === "onset" ? "on-set" : "fittings"} entry
                            </div>
                            <div className="text-sm text-white mt-1 truncate">
                                {characterName || "Character"}
                            </div>
                        </div>
                        <button
                            data-testid="state-entry-cancel"
                            type="button"
                            onClick={cancelPending}
                            className="tap h-9 w-9 rounded-md text-neutral-400 hover:text-white hover:bg-white/5 inline-flex items-center justify-center"
                        >
                            <X size={16} />
                        </button>
                    </div>

                    {/* Iter-157J.3 — Continuity state chips
                        (Before/During/After/Reset/Custom) only make
                        sense for scene-anchored on-set entries. For
                        Fittings (Unassigned) uploads the H&M user
                        does not have a "before what?" frame of
                        reference, so we hide the entire chip row
                        + custom-label Input. The state value is
                        pinned to "custom" with no custom_label on
                        save (see save() handler). */}
                    {!isPrep ? (
                        <>
                            <div className="flex flex-wrap gap-2">
                                {STATE_LABELS.map((l) => {
                                    const active = label === l.id;
                                    return (
                                        <button
                                            key={l.id}
                                            type="button"
                                            data-testid={`state-label-${l.id}`}
                                            onClick={() => setLabel(l.id)}
                                            className={`tap min-tap px-3 rounded-full text-xs border transition-colors ${
                                                active
                                                    ? "bg-primary text-primary-foreground border-primary"
                                                    : "bg-secondary text-neutral-300 border-white/10 hover:bg-white/10"
                                            }`}
                                        >
                                            {l.label}
                                        </button>
                                    );
                                })}
                            </div>

                            {label === "custom" ? (
                                <Input
                                    data-testid="state-custom-label"
                                    value={customLabel}
                                    onChange={(e) => setCustomLabel(e.target.value)}
                                    placeholder="Custom label (e.g. Wet, Bloodied, Take 2)"
                                    maxLength={40}
                                    className="h-11 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                                />
                            ) : null}
                        </>
                    ) : (
                        <Input
                            data-testid="state-look"
                            value={look}
                            onChange={(e) => setLook(e.target.value)}
                            placeholder="Look label (optional) — e.g. Hero, Trailer, Day 1"
                            maxLength={60}
                            className="h-11 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                        />
                    )}

                    <Textarea
                        data-testid="state-note"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Add a note… (optional)"
                        rows={3}
                        maxLength={2000}
                        className="bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary resize-none"
                    />

                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={cancelPending}
                            disabled={uploading}
                            className="min-tap flex-1"
                            data-testid="state-entry-discard"
                        >
                            Discard
                        </Button>
                        <Button
                            type="button"
                            onClick={save}
                            disabled={uploading}
                            className="min-tap flex-1"
                            data-testid="state-entry-save"
                        >
                            {uploading ? (
                                <>
                                    <Loader2 size={14} className="mr-2 animate-spin" /> Uploading…
                                </>
                            ) : (
                                "Save entry"
                            )}
                        </Button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="space-y-2" data-testid="state-stream-loading">
                    {[0, 1].map((i) => (
                        <div
                            key={i}
                            className="h-20 rounded-xl bg-card border border-white/10 animate-pulse"
                        />
                    ))}
                </div>
            ) : entries.length === 0 ? (
                // Iter-157J.4 — When a draft form is already open
                // (pendingFile truthy) we must NOT also render the
                // prominent empty-state CTA below it. Otherwise the
                // user sees a "dead" Upload Fitting Photos button
                // sitting under their in-flight form that appears to
                // do nothing when tapped (pickFile short-circuits
                // because pendingFile is set). Suppress the CTA
                // entirely while the form is the active surface;
                // it returns automatically once the draft is saved
                // or discarded (entries.length becomes >0 OR back
                // to 0 with no pendingFile).
                pendingFile ? null :
                // Iter-157J — Fittings (Unassigned) prominent empty-
                // state CTA. Replaces the orphan "No entries yet.
                // Capture one to get started." line that gave the
                // user nothing tappable. When uploads are allowed and
                // we're in prep mode, we render a big amber card the
                // user can tap directly to start the upload. The
                // subtext spells out the no-scene-needed promise so
                // a Hair & Makeup artist instantly understands the
                // workflow. When viewOnly OR onset mode, we keep the
                // calm passive line because there's no action to take.
                !viewOnly && mode === "prep" ? (
                    <button
                        type="button"
                        data-testid="state-stream-empty-upload"
                        onClick={pickFile}
                        className="tap w-full text-left rounded-xl border border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10 p-5 transition-colors"
                    >
                        <div className="flex items-center gap-3">
                            <div className="h-11 w-11 shrink-0 rounded-full bg-amber-400/20 text-amber-200 flex items-center justify-center">
                                <ImageIcon size={18} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="text-white font-semibold leading-tight text-base">
                                    Upload Fitting Photos
                                </div>
                                <div className="text-[11px] text-neutral-400 mt-1 leading-relaxed">
                                    No scene needed — add fitting / prep
                                    captures for{" "}
                                    <span className="text-neutral-200">
                                        {characterName || "this character"}
                                    </span>
                                    . You can assign them to scenes later.
                                </div>
                            </div>
                        </div>
                    </button>
                ) : (
                    <p
                        data-testid="state-stream-empty"
                        className="text-sm text-neutral-500 text-center py-5 rounded-xl border border-dashed border-white/10"
                    >
                        {mode === "prep"
                            ? "No fitting photos yet."
                            : "No entries yet. Capture one to get started."}
                    </p>
                )
            ) : (
                <ul data-testid="state-stream-list" className="space-y-2">
                    {entries.map((e) => {
                        const canDelete = e.created_by === user?.id || isOwner;
                        return (
                            <li key={e.id}>
                                <div
                                    className="flex gap-3 rounded-xl border border-white/10 bg-card p-3 tap hover:bg-white/5"
                                    data-testid={`state-entry-${e.id}`}
                                >
                                    <Link
                                        to={`${routeBase}/${projectId}/state-entries/${e.id}`}
                                        state={
                                            backHref
                                                ? {
                                                      // Origin-aware back:
                                                      // ImageDetail will
                                                      // route the user
                                                      // back to whichever
                                                      // page hosted this
                                                      // stream (scene
                                                      // detail, character
                                                      // panel in Fittings,
                                                      // etc.).
                                                      backHref,
                                                      backLabel: backLabel || "Back",
                                                  }
                                                : undefined
                                        }
                                        className="flex gap-3 flex-1 min-w-0"
                                        data-testid={`state-entry-open-${e.id}`}
                                    >
                                        <img
                                            src={entryThumbSrc(projectId, e, apiBase)}
                                            alt={labelFor(e)}
                                            className="h-16 w-16 rounded-lg object-cover border border-white/10"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                {/* Iter-157J.4 — In Fittings mode
                                                    the state_label is pinned to
                                                    "custom" by the form (chips are
                                                    hidden). Rendering a "CUSTOM"
                                                    pill on the saved entry card
                                                    is the same meaningless noise
                                                    we just removed from the form,
                                                    so hide it here too. Onset
                                                    entries continue to show the
                                                    pill — that pill carries real
                                                    continuity meaning (Before /
                                                    During / After / Reset). */}
                                                {!isPrep ? (
                                                    <span className="text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 rounded-full bg-secondary text-neutral-200">
                                                        {labelFor(e)}
                                                    </span>
                                                ) : null}
                                                <span className="text-[10px] text-neutral-500">
                                                    {formatRelativeTime(e.created_at)}
                                                </span>
                                            </div>
                                            <div className="text-sm text-white truncate mt-1">
                                                {firstLine(e.note) || (
                                                    <span className="text-neutral-500 italic">
                                                        No note
                                                    </span>
                                                )}
                                            </div>
                                            {/* Iter-118.15 — Look surfaced
                                                directly under the note in
                                                the prep/onset stream so the
                                                continuity reference is
                                                visible at a glance. */}
                                            <div
                                                data-testid={`state-stream-look-${e.id}`}
                                                className="text-xs mt-0.5 truncate"
                                            >
                                                {e.look ? (
                                                    <>
                                                        <span className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 mr-1.5">
                                                            Look
                                                        </span>
                                                        <span className="text-primary font-medium">
                                                            {e.look}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <span className="text-neutral-500 italic">
                                                        No look set
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-[10px] text-neutral-500 truncate mt-0.5">
                                                {e.created_by_name || "Someone"}
                                            </div>
                                        </div>
                                    </Link>
                                    {canDelete ? (
                                        <button
                                            type="button"
                                            data-testid={`state-entry-delete-${e.id}`}
                                            onClick={() => removeEntry(e)}
                                            title="Delete entry"
                                            className="tap h-9 w-9 rounded-md text-neutral-400 hover:text-red-300 hover:bg-red-500/10 inline-flex items-center justify-center self-start"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    ) : null}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
            <ConfirmDialog
                open={Boolean(pendingDelete)}
                onOpenChange={(v) => { if (!v && !deleting) setPendingDelete(null); }}
                title="Delete this entry?"
                description="This cannot be undone. The photo and its note will be removed permanently."
                confirmLabel="Delete entry"
                cancelLabel="Keep entry"
                onConfirm={confirmRemoveEntry}
                busy={deleting}
                tone="danger"
                testidPrefix="confirm-delete-state-entry"
            />
        </section>
    );
});

export default StateStream;
